import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type {
  NormalizedTemplate,
  PapaParsePassthrough,
} from '../types/template';
import { parseCSV } from '../services/parser';
import {
  validateRows,
  type BatchValidationResult,
} from '../validators/row-validator';
import {
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_ROWS,
  PROTECTED_PAPAPARSE_KEYS,
} from '../constants/default';

export type { CsvRow } from '../services/parser';

export type ParseResult = {
  status: 'completed' | 'aborted';
  error: Error | null;
};

/**
 * Reactive controller for CSV parsing + validation.
 *
 * After a successful `parse()`, read `validationResult` for all row data.
 * The method return is just a status signal — follows Lit controller convention.
 */
export class ParserController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _abort: AbortController | null = null;

  isRunning = false;
  validationResult: BatchValidationResult | null = null;
  error: Error | null = null;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  async parse(file: File, template: NormalizedTemplate): Promise<ParseResult> {
    this._clearState();
    this._abort?.abort();
    this._abort = new AbortController();
    const { signal } = this._abort;

    this.isRunning = true;
    this.host.requestUpdate();

    const { limits, parserOptions } = template.importerConfig;
    const maxFileSize = limits.maxFileSize ?? DEFAULT_MAX_FILE_SIZE;
    const maxRows = limits.maxRows ?? DEFAULT_MAX_ROWS;

    // File size guard
    if (file.size > maxFileSize) {
      return this._complete(
        new Error(
          `File size ${file.size} exceeds the maximum allowed size of ${maxFileSize} bytes.`,
        ),
      );
    }

    const safeOptions = this._stripProtectedKeys(parserOptions);

    try {
      const result = await parseCSV(file, safeOptions, maxRows, signal);

      if (signal.aborted) return this._aborted();

      // Max rows guard
      if (result.dataRowCount > maxRows) {
        return this._complete(
          new Error(
            `File contains ${result.dataRowCount.toString()} data rows, which exceeds the maximum of ${maxRows.toString()} rows.`,
          ),
        );
      }

      this.validationResult = validateRows(result.rows, template.columns);

      return this._complete(null);
    } catch (err) {
      return this._complete(
        err instanceof Error ? err : new Error(String(err)),
      );
    }
  }

  reset(): void {
    this._clearState();
    this.isRunning = false;
    this._abort?.abort();
    this._abort = null;
    this.host.requestUpdate();
  }

  hostDisconnected(): void {
    this.isRunning = false;
    this._abort?.abort();
    this._abort = null;
  }

  // --- Private ---

  private _complete(err: Error | null): ParseResult {
    this.error = err;
    this.isRunning = false;
    this._abort = null;
    this.host.requestUpdate();
    return { status: 'completed', error: err };
  }

  private _aborted(): ParseResult {
    this.isRunning = false;
    this._abort = null;
    this.host.requestUpdate();
    return { status: 'aborted', error: null };
  }

  private _clearState(): void {
    this.validationResult = null;
    this.error = null;
  }

  private _stripProtectedKeys(
    parserOptions: NormalizedTemplate['importerConfig']['parserOptions'],
  ): PapaParsePassthrough {
    if (!parserOptions) return {};
    return Object.fromEntries(
      Object.entries(parserOptions).filter(
        ([key]) =>
          !(PROTECTED_PAPAPARSE_KEYS as readonly string[]).includes(key),
      ),
    ) as PapaParsePassthrough;
  }
}
